import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";

/**
 * Phase 13.5 — Cmd-K (Ctrl-K on Windows/Linux) AI prompt palette.
 *
 * The IA review (DECISIONS.md 2026-05-09 IA review block) dropped AI
 * from the sidebar; this palette restores discoverability as a global
 * keyboard shortcut. Implementation is a stub for v1: opens a centered
 * modal with prompt suggestions + a free-text composer that, on
 * submit, navigates to `/ai?prompt=<encoded>` (the existing deep-link
 * pattern at /ai pre-fills the composer). Phase 14 turns this into an
 * inline streaming response — same component, more guts.
 *
 * Keyboard binding: ignores Cmd-K when focus is in an `<input>`,
 * `<textarea>`, or `[contenteditable]` — common UX expectation, lets
 * users press Cmd-K while typing inside text fields without surprise.
 */

const SUGGESTIONS: readonly string[] = [
  "What's my financial picture?",
  "When can we retire?",
  "What bills are coming due this week?",
  "Where should the next dollar go?",
  "Has anything in my spending drifted up this year?",
  "Show me the saving-rate trend.",
];

export function CommandKPalette() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  // Global keyboard binding.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isCmdK =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (!isCmdK) {
        if (e.key === "Escape" && open) setOpen(false);
        return;
      }
      // Don't hijack Cmd-K while focused inside text inputs.
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName?.toLowerCase();
        if (
          tag === "input" ||
          tag === "textarea" ||
          t.isContentEditable
        ) {
          return;
        }
      }
      e.preventDefault();
      setOpen((v) => !v);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus the input when opening.
  useEffect(() => {
    if (open) {
      // Defer one frame so the input has actually mounted.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  function submit(prompt: string) {
    const trimmed = prompt.trim();
    if (trimmed.length === 0) return;
    setOpen(false);
    setValue("");
    void navigate(`/ai?prompt=${encodeURIComponent(trimmed)}`);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    submit(value);
  }

  if (!open) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={() => setOpen(false)}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        aria-label="Ask Hearth"
        className="fixed left-1/2 top-24 z-50 w-full max-w-2xl -translate-x-1/2 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-surface)] shadow-2xl"
      >
        <form onSubmit={onSubmit} className="border-b border-[var(--color-border-default)]">
          <div className="flex items-center gap-3 px-5 py-4">
            <SparkIcon />
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Ask Hearth anything…"
              className="flex-1 bg-transparent text-base text-[var(--color-text)] placeholder:text-[var(--color-faint)] focus:outline-none"
            />
            <kbd className="rounded border border-[var(--color-border-default)] bg-[var(--color-card)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-muted)]">
              Esc
            </kbd>
          </div>
        </form>
        <div className="px-3 py-2">
          <p className="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-[var(--color-faint)]">
            Suggested prompts
          </p>
          <ul>
            {SUGGESTIONS.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onClick={() => submit(s)}
                  className="w-full rounded-md px-3 py-2 text-left text-sm text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
                >
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <footer className="flex items-center justify-between border-t border-[var(--color-border-default)] px-5 py-2 text-xs text-[var(--color-muted)]">
          <span>Press Enter to ask in the AI page.</span>
          <span>
            <kbd className="rounded border border-[var(--color-border-default)] bg-[var(--color-card)] px-1.5 py-0.5 text-[10px] font-medium">
              ⌘K
            </kbd>{" "}
            anywhere to open
          </span>
        </footer>
      </div>
    </>
  );
}

function SparkIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5 text-[var(--color-accent)]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.5 5.5l2.5 2.5M16 16l2.5 2.5M5.5 18.5 8 16M16 8l2.5-2.5" />
    </svg>
  );
}
